import { graphql } from 'react-relay';
/* This component is used to select what permissions to use when creating a new Space or
 to copy into an existing Space: default permissions, private permissions, or copy the
 permissions from an existing Space */
import React, { Fragment, useMemo, useState } from 'react';
import type { MessageDescriptor } from 'react-intl-next';
import { defineMessages } from 'react-intl-next';
import { styled } from '@compiled/react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { createAnalyticsEventFactory } from '@confluence/analytics/entry-points/CreateAnalyticsEventFactory.ts';
import { getUserPrivilegeFromQuery } from '@confluence/current-user/entry-points/getUserPrivilegeFromQuery.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

import type { CopySpacePermissionsSelectorQuery as CopySpacePermissionsSelectorQuery$data } from './__types__/CopySpacePermissionsSelectorQuery.ts';
import {
	OPTION_SELECT_ACTION_SUBJECT,
	SOURCE_SPACE_SELECT_ACTION_SUBJECT,
	OPTION_SELECT_DROPDOWN_OPEN_ACTION_SUBJECT_ID,
	SOURCE_SPACE_SELECTED_SUBJECT_ID,
	fireUIClickEvent,
	getEventActionSubjectId,
} from './AnalyticsEvents.tsx';
import { Option } from './Option.ts';
import type { OptionSelectProps } from './OptionSelect.tsx';
import { OptionSelect } from './OptionSelect.tsx';
import type { SourceSpaceSelectorProps, SpaceOptionType } from './SourceSpaceSelector.tsx';
import { SourceSpaceSelector } from './SourceSpaceSelector.tsx';
import { LabeledSelect } from './LabeledSelect.tsx';
const CopySpacePermissionsSelectorQuery = graphql`
	query CopySpacePermissionsSelectorQuery {
		user: confluence_atlassianUser(current: true) {
			id
			confluence {
				operations {
					operation
					targetType
				}
			}
		}
		isSiteAdmin
		hasUserAccessAdminRole
	}
`;
export const i18n = defineMessages({
	sourceSpaceSelectorPlaceholder: {
		id: 'copy-space-permissions.space-selector.placeholder',
		defaultMessage: 'Choose a space',
		description:
			'The placeholder text for the dropdown where the user selects the space to copy permissions from into another space',
	},
	sourceSpaceSelectorLabel: {
		id: 'copy-space-permissions.space-selector.prompt',
		defaultMessage: 'Copy from',
		description:
			'The label for the dropdown where the user selects the space to copy permissions from into another space',
	},
});

export const EMPTY_SPACE = {
	isRestricted: false,
	containsExternalCollaborators: false,
	key: '',
	name: '',
	isAdmin: false,
	id: '',
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FlexRow = styled.div({
	alignItems: 'center',
	display: 'flex',
	flexDirection: 'row',
});

export type CopySpacePermissionsSourceSpace = {
	isRestricted: boolean;
	containsExternalCollaborators: boolean;
	key: string;
	name: string;
	isAdmin: boolean;
	id: string;
};

export type CopySpacePermissionsSelection = {
	option?: Option;
	sourceSpace: CopySpacePermissionsSourceSpace;
};

export type CopySpacePermissionsSelectorProps = {
	analyticsEventSourceName: OptionSelectProps['analyticsEventSourceName'];
	defaultOption?: Option;
	defaultSpaceKey?: string;
	isDisabled?: boolean;
	label?: MessageDescriptor;
	onChange: (selections: CopySpacePermissionsSelection) => void;
	options: Option[];
	showUpsell?: boolean;
	sourceSpaceSelectorValidationState?: SourceSpaceSelectorProps['validationState'];
	filterSourceSpaceOptions?: (option: SpaceOptionType) => boolean;
	renderGuestsWarning?: boolean;
	fullWidth?: boolean;
	validationPlaceholder?: boolean;
	optionSelectInputId?: string;
	allowOnlyAdminSpaces?: boolean;
	labelTextTransform?: string;
};

export function CopySpacePermissionsSelector({
	analyticsEventSourceName,
	defaultOption,
	defaultSpaceKey,
	isDisabled,
	label,
	onChange,
	options,
	showUpsell,
	filterSourceSpaceOptions,
	sourceSpaceSelectorValidationState = 'default',
	renderGuestsWarning,
	fullWidth,
	validationPlaceholder,
	optionSelectInputId,
	allowOnlyAdminSpaces,
}: CopySpacePermissionsSelectorProps) {
	const [option, setOption] = useState(defaultOption);
	const [sourceSpace, setSourceSpace] = useState<CopySpacePermissionsSourceSpace>(EMPTY_SPACE);

	/* Bind createAnalyticsEvent to the event Source, edition, and userPrivilege so that these two pieces of
    data go out with all analytics events */
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { edition } = useSessionData();
	const { data } = useQueryInterop<CopySpacePermissionsSelectorQuery$data>(
		CopySpacePermissionsSelectorQuery,
	);
	const wrappedCreateAnalyticsEvent = useMemo(() => {
		const userPrivilege = getUserPrivilegeFromQuery(data);
		return createAnalyticsEventFactory(createAnalyticsEvent, analyticsEventSourceName, {
			...(edition && { edition }),
			...(userPrivilege && { userPrivilege }),
		});
	}, [analyticsEventSourceName, createAnalyticsEvent, data, edition]);

	const onCopySpacePermissionsOptionMenuOpen = () => {
		fireUIClickEvent(
			wrappedCreateAnalyticsEvent,
			OPTION_SELECT_ACTION_SUBJECT,
			OPTION_SELECT_DROPDOWN_OPEN_ACTION_SUBJECT_ID,
		);
	};

	const onCopySpacePermissionsOptionChange = (option: Option) => {
		setOption(option);
		// Always clear the source Space Key, as the SpaceSelector has no initial selection
		if (sourceSpace !== EMPTY_SPACE) {
			setSourceSpace(EMPTY_SPACE);
		}

		fireUIClickEvent(
			wrappedCreateAnalyticsEvent,
			OPTION_SELECT_ACTION_SUBJECT,
			getEventActionSubjectId(option),
		);
		onChange({ option, sourceSpace: EMPTY_SPACE });
	};

	const onSpacePermissionsSourceChange = ({
		isRestricted,
		containsExternalCollaborators,
		spaceKey,
		spaceName,
		isAdmin,
		id,
	}) => {
		if (sourceSpace.key !== spaceKey || sourceSpace.name !== spaceName) {
			const nextSourceSpace = {
				isRestricted,
				containsExternalCollaborators,
				isAdmin,
				key: spaceKey,
				name: spaceName,
				id,
			};

			setSourceSpace(nextSourceSpace);

			fireUIClickEvent(
				wrappedCreateAnalyticsEvent,
				SOURCE_SPACE_SELECT_ACTION_SUBJECT,
				SOURCE_SPACE_SELECTED_SUBJECT_ID,
			);
			onChange({ option, sourceSpace: nextSourceSpace });
		}
	};

	const isSourceSpaceInvalid = !isSourceSpaceValid({
		option,
		sourceSpace,
		allowOnlyAdminSpaces,
	});

	if (
		sourceSpaceSelectorValidationState === 'default' &&
		sourceSpace !== EMPTY_SPACE &&
		isSourceSpaceInvalid
	) {
		// DON'T show a validation error if the user has not selected a space yet
		// even though no space selected is an invalid state:
		sourceSpaceSelectorValidationState = 'error';
	}

	return (
		<Fragment>
			<FlexRow>
				<OptionSelect
					analyticsEventSourceName={analyticsEventSourceName}
					isDisabled={isDisabled}
					label={label}
					onChange={onCopySpacePermissionsOptionChange}
					onMenuOpen={onCopySpacePermissionsOptionMenuOpen}
					options={options}
					showUpsell={showUpsell}
					value={option}
					fullWidth={fullWidth}
					inputId={optionSelectInputId}
				/>
			</FlexRow>
			{option === Option.COPY_PERMISSIONS_FROM_ANOTHER_SPACE ? (
				<LabeledSelect>
					<SourceSpaceSelector
						onChange={onSpacePermissionsSourceChange}
						label={label ? i18n.sourceSpaceSelectorLabel : undefined}
						placeholder={i18n.sourceSpaceSelectorPlaceholder}
						validationState={sourceSpaceSelectorValidationState}
						filterSourceSpaceOptions={filterSourceSpaceOptions}
						renderGuestsWarning={renderGuestsWarning}
						fullWidth={fullWidth}
						validationPlaceholder={validationPlaceholder}
						defaultSpaceKey={defaultSpaceKey}
						allowOnlyAdminSpaces={allowOnlyAdminSpaces}
						showSpaceLabel
						labelTextTransform="none"
					/>
				</LabeledSelect>
			) : null}
		</Fragment>
	);
}

type isSourceSpaceValidArgs = {
	option?: Option;
	sourceSpace: CopySpacePermissionsSourceSpace;
	allowOnlyAdminSpaces?: boolean;
	targetSpaceKey?: string;
};

export function isSourceSpaceValid({
	option,
	sourceSpace,
	allowOnlyAdminSpaces,
	targetSpaceKey,
}: isSourceSpaceValidArgs): boolean {
	return (
		option != Option.COPY_PERMISSIONS_FROM_ANOTHER_SPACE ||
		(sourceSpace &&
			Boolean(sourceSpace.key) &&
			!sourceSpace.isRestricted &&
			(allowOnlyAdminSpaces ? sourceSpace.isAdmin : true) &&
			(targetSpaceKey === undefined || targetSpaceKey !== sourceSpace.key))
	);
}
